import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ImageOff, Maximize2, X } from 'lucide-react';
import { shotById } from '../../data/market/screenshots';
import { SHOT_CAPTION } from '../../data/market/config';
import type { ShotId } from '../../types/market';
const EASE = [0.23, 1, 0.32, 1] as const;

/**
 * Captura del manual en contexto: miniatura recortada por altura (sin deformar) y vista ampliada
 * con desplazamiento para ver la pantalla completa. Se cierra con Escape, el botón o tocando fuera.
 */
export function Screenshot({
  id
}: {
  id: ShotId;
}) {
  const shot = shotById(id);
  const [open, setOpen] = useState(false);
  const [failed, setFailed] = useState(false);
  const trigger = useRef<HTMLButtonElement>(null);
  if (!shot) return null;
  if (failed) {
    return <figure className="my-5 flex items-center gap-3 rounded-2xl border border-dashed border-white/15 p-4 text-sm text-gray-400" data-id="element-1112" data-mp="12slyhk19lohl">
        <ImageOff className="h-5 w-5 shrink-0" aria-hidden="true" data-id="element-1113" data-mp="1vht7f91tbghp" />
        <span data-id="element-1114" data-mp="1qpt0gwnbrnbm">
          No se pudo cargar la captura <span className="font-mono text-gray-300" data-id="element-1115" data-mp="1qorkqevd6cnk">{shot.file}</span> ({shot.title}).
        </span>
      </figure>;
  }
  return <figure className="my-5 break-inside-avoid" data-id="element-1116" data-mp="1hpmr9mynt9i9">
      <button ref={trigger} type="button" onClick={() => setOpen(true)} aria-label={`Ampliar captura: ${shot.title}`} className="group relative block w-full overflow-hidden rounded-2xl border border-white/10 bg-[#0A1628] text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D4AF37]" data-id="element-1117" data-mp="1hqxrxdwt6155">
        <div className="max-h-[420px] overflow-hidden print:max-h-none" data-id="element-1118" data-mp="20ge6h55bvu4k">
          <img src={shot.url} alt={shot.alt} loading="lazy" decoding="async" onError={() => setFailed(true)} className="block h-auto w-full" data-id="element-1119" data-mp="20i8ks0m3gnzo" />
        </div>
        <span className="absolute bottom-3 right-3 inline-flex items-center gap-1.5 rounded-full bg-[#050b14] px-3 py-1.5 text-xs font-semibold text-white ring-1 ring-white/15 transition-colors duration-150 group-hover:text-[#D4AF37] print:hidden" data-id="element-1120" data-mp="ozxmcyitwpbd">
          <Maximize2 className="h-3.5 w-3.5" aria-hidden="true" data-id="element-1121" data-mp="vev3g0cxzoby" />
          Ver captura completa
        </span>
      </button>
      <figcaption className="mt-2 text-xs text-gray-500" data-id="element-1122" data-mp="liibb6x1pijb">
        <span className="font-semibold text-gray-400" data-id="element-1123" data-mp="ll4jn8rrg22l">{shot.title}</span> · {SHOT_CAPTION}
      </figcaption>
      <Lightbox open={open} onClose={() => {
      setOpen(false);
      trigger.current?.focus();
    }} url={shot.url} alt={shot.alt} title={shot.title} data-id="element-1124" data-mp="7ptqbzt5zp0c" />
    </figure>;
}
function Lightbox({
  open,
  onClose,
  url,
  alt,
  title
}: {
  open: boolean;
  onClose: () => void;
  url: string;
  alt: string;
  title: string;
}) {
  const reduce = useReducedMotion();
  const closeBtn = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeBtn.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'Tab') {
        e.preventDefault();
        closeBtn.current?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);
  return createPortal(<AnimatePresence data-id="element-1125" data-mp="3p9k4h91pmn68">
      {open && <motion.div role="dialog" aria-modal="true" aria-label={`Captura ampliada: ${title}`} className="fixed inset-0 z-[80] flex flex-col bg-[#02060c]/95 print:hidden" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: reduce ? 0 : 0.2,
      ease: EASE
    }} onClick={onClose} data-id="element-1126" data-mp="3p8hj1gn5pcxp">
          <div className="flex shrink-0 items-center justify-between gap-3 border-b border-white/10 px-4 py-3" data-id="element-1127" data-mp="bt2ywe2pn4xl">
            <p className="min-w-0 truncate text-sm font-semibold text-white" data-id="element-1128" data-mp="21cku4hxdsgbr">{title}</p>
            <button ref={closeBtn} type="button" onClick={onClose} className="inline-flex h-10 items-center gap-2 rounded-full border border-white/15 px-4 text-sm font-semibold text-white transition-colors duration-150 hover:border-[#D4AF37]/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D4AF37]" data-id="element-1129" data-mp="21aqdhft57adh">
              <X className="h-4 w-4" aria-hidden="true" data-id="element-1130" data-mp="1ie8eqefvma7q" />
              Cerrar <span className="hidden text-gray-400 sm:inline" data-id="element-1131" data-mp="1ig1ow3pp7ckh">(Esc)</span>
            </button>
          </div>
          <motion.div className="min-h-0 flex-1 overflow-auto overscroll-contain p-3 sm:p-6" initial={{
        opacity: 0,
        scale: reduce ? 1 : 0.98
      }} animate={{
        opacity: 1,
        scale: 1
      }} exit={{
        opacity: 0,
        scale: reduce ? 1 : 0.98
      }} transition={{
        duration: reduce ? 0 : 0.22,
        ease: EASE
      }} data-id="element-1132" data-mp="1redcdwv9pu8p">
            <img src={url} alt={alt} onClick={e => e.stopPropagation()} className="mx-auto block h-auto w-full max-w-5xl rounded-xl" data-id="element-1133" data-mp="37rc0v296qvdp" />
            <p className="mx-auto mt-3 max-w-5xl text-xs text-gray-400" data-id="element-1134" data-mp="3hyeifemvjxrt">{SHOT_CAPTION}</p>
          </motion.div>
        </motion.div>}
    </AnimatePresence>, document.body);
}